/**
 * User Activity API
 *
 * Create events (fire-and-forget from the tracker) and list recent events
 * for the Sales Manager admin page.
 */

import { dataverseClient, TABLES, type QueryOptions } from '@shared/api';
import { assertDateLiteral } from '@shared/lib';
import type {
  UserActivity,
  CreateUserActivityInput,
  UserActivitySummary,
} from '../model/types';

interface UserActivityEntity {
  vba_useractivityid: string;
  vba_name?: string;
  vba_userobjectid?: string;
  vba_username?: string;
  vba_useremail?: string;
  vba_app?: string;
  vba_eventtype?: string;
  vba_area?: string;
  vba_path?: string;
  vba_sessionid?: string;
  vba_referrerpath?: string;
  vba_queryparams?: string;
  vba_durationms?: number;
  vba_context?: string;
  createdon?: string;
  [key: string]: unknown;
}

const SELECT_FIELDS = [
  'vba_useractivityid',
  'vba_name',
  'vba_userobjectid',
  'vba_username',
  'vba_useremail',
  'vba_app',
  'vba_eventtype',
  'vba_area',
  'vba_path',
  'vba_sessionid',
  'vba_referrerpath',
  'vba_queryparams',
  'vba_durationms',
  'vba_context',
  'createdon',
];

function mapToDomain(entity: UserActivityEntity): UserActivity {
  return {
    id: entity.vba_useractivityid,
    name: entity.vba_name ?? '',
    userObjectId: entity.vba_userobjectid ?? '',
    userName: entity.vba_username ?? '',
    userEmail: entity.vba_useremail ?? '',
    app: entity.vba_app ?? '',
    eventType: entity.vba_eventtype ?? '',
    area: entity.vba_area ?? undefined,
    path: entity.vba_path ?? undefined,
    sessionId: entity.vba_sessionid ?? undefined,
    referrerPath: entity.vba_referrerpath ?? undefined,
    queryParams: entity.vba_queryparams ?? undefined,
    durationMs: entity.vba_durationms ?? undefined,
    context: entity.vba_context ?? undefined,
    createdOn: entity.createdon ?? '',
  };
}

function truncate(value: string | undefined, max: number): string | undefined {
  if (value === undefined || value === null) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

function buildName(input: CreateUserActivityInput): string {
  const who = input.userEmail || input.userName || input.userObjectId;
  const what = input.eventType;
  const where = input.area ? ` · ${input.area}` : '';
  const when = new Date().toISOString();
  return truncate(`${what} · ${who}${where} · ${when}`, 400) ?? '';
}

async function create(input: CreateUserActivityInput): Promise<void> {
  const payload: Record<string, unknown> = {
    vba_name: buildName(input),
    vba_userobjectid: truncate(input.userObjectId, 50),
    vba_username: truncate(input.userName, 200),
    vba_useremail: truncate(input.userEmail, 200),
    vba_app: truncate(input.app, 20),
    vba_eventtype: truncate(input.eventType, 30),
  };
  if (input.area) payload.vba_area = truncate(input.area, 100);
  if (input.path) payload.vba_path = truncate(input.path, 500);
  if (input.sessionId) payload.vba_sessionid = truncate(input.sessionId, 50);
  if (input.referrerPath) payload.vba_referrerpath = truncate(input.referrerPath, 500);
  if (input.queryParams) payload.vba_queryparams = truncate(input.queryParams, 500);
  if (typeof input.durationMs === 'number') {
    payload.vba_durationms = Math.max(0, Math.min(86_400_000, Math.round(input.durationMs)));
  }
  if (input.context) {
    try {
      payload.vba_context = truncate(JSON.stringify(input.context), 4000);
    } catch {
      /* non-serializable, skip */
    }
  }

  await dataverseClient.create<UserActivityEntity>(TABLES.USER_ACTIVITY, payload, {
    returnRepresentation: false,
  });
}

/**
 * Pages of roughly 5000 rows each. Ten of them is ~50k events, well past a
 * busy 30-day window, and `truncated` tells the caller when even that was not
 * enough so the page can say so instead of quietly showing less.
 */
const MAX_PAGES = 10;

export interface RecentActivity {
  events: UserActivity[];
  /** True when the page cap cut the window short: the oldest events are missing. */
  truncated: boolean;
}

/**
 * List recent events, optionally filtered to a window (since, until).
 *
 * This used to pass `top: 2000` with no paging, so a 30-day request returned
 * the most recent 2000 events and nothing said otherwise. That is what made
 * "last 30 days" show ten days of sign-ins (ADO #1305) and what made the user
 * and event counts on the admin page wrong for the older half of every window.
 */
async function listRecent(sinceIso?: string, untilIso?: string): Promise<RecentActivity> {
  const options: QueryOptions = {
    select: SELECT_FIELDS,
    orderBy: 'createdon desc',
  };
  const filters: string[] = [];
  if (sinceIso) filters.push(`createdon ge ${assertDateLiteral(sinceIso, 'sinceIso')}`);
  if (untilIso) filters.push(`createdon le ${assertDateLiteral(untilIso, 'untilIso')}`);
  if (filters.length) options.filter = filters.join(' and ');
  const { records, truncated } = await dataverseClient.listPaged<UserActivityEntity>(
    TABLES.USER_ACTIVITY,
    options,
    MAX_PAGES,
  );
  return { events: records.map(mapToDomain), truncated };
}

/**
 * Build a per-user summary from a flat list of events.
 * Client-side roll-up keeps the server query simple.
 *
 * Login count comes straight from 'login' events again. Those rows are now
 * written server-side at the OAuth callback — one per real sign-in, tagged
 * vera-shell — so they're reliable and genuinely one-per-login, with no need
 * for the old distinct-sessionId workaround that effectively counted browser
 * tabs. The admin page keeps login/logout events visible across app filters
 * (login is a platform event, not a per-module one) so this count stays
 * correct even when the view is narrowed to a single module.
 */
function summarize(events: UserActivity[]): UserActivitySummary[] {
  const byUser = new Map<string, UserActivitySummary>();
  for (const e of events) {
    const key = e.userObjectId || e.userEmail || e.userName;
    if (!key) continue;
    let agg = byUser.get(key);
    if (!agg) {
      agg = {
        userObjectId: e.userObjectId,
        userName: e.userName,
        userEmail: e.userEmail,
        loginCount: 0,
        lastLoginAt: undefined,
        lastSeenAt: e.createdOn,
        areasVisited: [],
        totalEvents: 0,
      };
      byUser.set(key, agg);
    }
    agg.totalEvents += 1;
    if (e.createdOn > agg.lastSeenAt) agg.lastSeenAt = e.createdOn;
    // Keep the most recent name/email for display
    if (e.createdOn === agg.lastSeenAt) {
      if (e.userName) agg.userName = e.userName;
      if (e.userEmail) agg.userEmail = e.userEmail;
    }
    if (e.eventType === 'login') {
      agg.loginCount += 1;
      if (!agg.lastLoginAt || e.createdOn > agg.lastLoginAt) {
        agg.lastLoginAt = e.createdOn;
      }
    }
    if ((e.eventType === 'area_visit' || e.eventType === 'page_view') && e.area) {
      // Keyed by app AND area. Keyed on the label alone, the five modules that
      // each call their landing page 'Dashboard' collapsed into one chip whose
      // count belonged to nobody.
      const existing = agg.areasVisited.find((a) => a.area === e.area && a.app === e.app);
      if (existing) {
        existing.count += 1;
        if (e.createdOn > existing.lastAt) existing.lastAt = e.createdOn;
      } else {
        agg.areasVisited.push({ app: e.app, area: e.area, count: 1, lastAt: e.createdOn });
      }
    }
  }
  for (const agg of byUser.values()) {
    agg.areasVisited.sort((a, b) => b.count - a.count);
  }
  return Array.from(byUser.values()).sort((a, b) =>
    a.lastSeenAt < b.lastSeenAt ? 1 : a.lastSeenAt > b.lastSeenAt ? -1 : 0,
  );
}

export const userActivityApi = {
  create,
  listRecent,
  summarize,
};
